/**
 * The border treatment for markdown rendered inside a message.
 *
 * Shared, because there are TWO Streamdown instances in a turn — the answer and
 * the reasoning steps — and a fix applied to one silently leaves the other
 * looking like the old version. That is exactly how the reasoning tables kept
 * their near-black borders after the answer's were fixed.
 *
 * WHY ANY OF THIS EXISTS. Streamdown styles its chrome with shadcn utilities —
 * `border-border`, `bg-background`, `bg-sidebar` — compiled by the CONSUMING
 * app's Tailwind. Caruso's palette defines no `border` colour, so `border-border`
 * emits NO rule at all and the border falls through to `currentColor`: a table
 * drew a near-black outline against the assistant text colour. Defining
 * `--border` does not help, because the class never existed to read it.
 *
 * Scoped to the chrome that actually draws borders, NOT `[&_*]`. That caught
 * every descendant, and `StatusBadge` sets `border-transparent` on itself — so an
 * `!important` neutral border was painted over each badge in an answer, in every
 * variant. Badges are divs, so the selectors have to name the table and code
 * elements rather than exclude a tag.
 */
export const MARKDOWN_CHROME = [
	"[&_thead]:!border-neutral-200 [&_tbody]:!border-neutral-200 [&_tr]:!border-neutral-200",
	"[&_th]:!border-neutral-200 [&_td]:!border-neutral-200",
	// §7: "dividers between header columns, no vertical dividers in the body, one
	// divider between rows." The last two already hold — Streamdown puts `divide-y`
	// on the table (the header/body rule) and on `tbody` (the row rules), and draws
	// no vertical borders at all. Only the header separators were missing, so they
	// are the only thing added here, and the trailing edge is dropped so the frame
	// is not doubled.
	"[&_thead_th]:!border-r [&_thead_th:last-child]:!border-r-0",
	// The scrollable frame Streamdown wraps a table in. Addressed through the
	// wrapper's data attribute so it cannot match a div inside a cell.
	"[&_[data-streamdown=table-wrapper]>div]:!border-neutral-200",
	"[&_pre]:!border-neutral-200 [&_blockquote]:!border-neutral-200 [&_hr]:!border-neutral-200",
	"dark:[&_thead]:!border-neutral-700 dark:[&_tbody]:!border-neutral-700 dark:[&_tr]:!border-neutral-700",
	"dark:[&_th]:!border-neutral-700 dark:[&_td]:!border-neutral-700",
	"dark:[&_[data-streamdown=table-wrapper]>div]:!border-neutral-700",
	"dark:[&_pre]:!border-neutral-700 dark:[&_blockquote]:!border-neutral-700 dark:[&_hr]:!border-neutral-700",
	// The table also carries a 1px border of its own, which doubles up with the
	// wrapper Streamdown puts around it and leaves a visible sliver between the two.
	// The wrapper owns the frame, so the table gets none. (No `border-collapse`
	// needed — Tailwind's preflight already collapses tables.)
	"[&_table]:!border-0",
	// ...and its own 8px corner is LARGER than the 6px frame that clips it, so the
	// header's grey curved away faster than the border and left a white sliver in
	// each top corner. Square the table and let the frame's radius do the rounding,
	// so the two cannot drift apart again.
	"[&_table]:!rounded-none",

	// ── Controls: copy / download / fullscreen ───────────────────────────────
	// Streamdown draws these as a 16px glyph in a 24px box coloured
	// `text-muted-foreground`, with no hover surface — and that colour is a shadcn
	// token Caruso never defines, so it lands on a neutral belonging to neither
	// palette. The message action row beside them is a ghost Button at `icon-sm`:
	// 32px box, 14px glyph, neutral-100 behind it on hover. Matched here so the
	// two rows read as one set of controls rather than two.
	//
	// The tooltip chip itself lives in `MarkdownControlTooltips`, not here: it has
	// to be portalled to escape the five clipping ancestors above a control.
	//
	// `:has(>svg)` is load-bearing, NOT decoration. Copy opens a dropdown of
	// Markdown / CSV / TSV, and those items are `button`s inside the same wrapper.
	// A plain descendant selector crushed all three into 32px squares and their
	// labels overlapped into one unreadable smear. Only the icon controls hold a
	// bare glyph, so that is what distinguishes them.
	//
	// `inline-flex` matters too: streamdown ships these as `inline-block` with
	// `p-1`, so forcing a 32px box left the 14px glyph sitting off-centre with the
	// hover fill visibly adrift of it. The box has to centre its own content.
	"[&_[data-streamdown=table-wrapper]_button:has(>svg)]:!inline-flex [&_[data-streamdown=table-wrapper]_button:has(>svg)]:!items-center [&_[data-streamdown=table-wrapper]_button:has(>svg)]:!justify-center",
	"[&_[data-streamdown=table-wrapper]_button:has(>svg)]:!size-8 [&_[data-streamdown=table-wrapper]_button:has(>svg)]:!p-0 [&_[data-streamdown=table-wrapper]_button:has(>svg)]:!rounded-md",
	"[&_[data-streamdown=table-wrapper]_button:has(>svg)]:!text-neutral-500 [&_[data-streamdown=table-wrapper]_button:has(>svg)]:!transition-colors",
	"[&_[data-streamdown=table-wrapper]_button:has(>svg):hover]:!bg-neutral-100 [&_[data-streamdown=table-wrapper]_button:has(>svg):hover]:!text-neutral-900",
	"[&_[data-streamdown=table-wrapper]_button>svg]:!size-3.5",
	"[&_[data-streamdown=code-block-actions]_button:has(>svg)]:!inline-flex [&_[data-streamdown=code-block-actions]_button:has(>svg)]:!items-center [&_[data-streamdown=code-block-actions]_button:has(>svg)]:!justify-center",
	"[&_[data-streamdown=code-block-actions]_button:has(>svg)]:!size-8 [&_[data-streamdown=code-block-actions]_button:has(>svg)]:!p-0 [&_[data-streamdown=code-block-actions]_button:has(>svg)]:!rounded-md",
	"[&_[data-streamdown=code-block-actions]_button:has(>svg)]:!text-neutral-500 [&_[data-streamdown=code-block-actions]_button:has(>svg)]:!transition-colors",
	"[&_[data-streamdown=code-block-actions]_button:has(>svg):hover]:!bg-neutral-100 [&_[data-streamdown=code-block-actions]_button:has(>svg):hover]:!text-neutral-900",
	"[&_[data-streamdown=code-block-actions]_button>svg]:!size-3.5",
	// The buttons matched, the rows around them did not. A code block's action row
	// is a floating chip — `bg-sidebar/80` over `border-sidebar`, two more tokens
	// Caruso never defines, so it fell back to Tailwind's own greys and drew a box
	// belonging to no palette. The table's row is bare, so this one is too, and the
	// cluster stops reading a size larger than its twin.
	"[&_[data-streamdown=code-block-actions]]:!border-0 [&_[data-streamdown=code-block-actions]]:!bg-transparent",
	"[&_[data-streamdown=code-block-actions]]:!p-0 [&_[data-streamdown=code-block-actions]]:!gap-1",
	"dark:[&_[data-streamdown=table-wrapper]_button:has(>svg):hover]:!bg-neutral-800 dark:[&_[data-streamdown=table-wrapper]_button:has(>svg):hover]:!text-neutral-50",
	"dark:[&_[data-streamdown=code-block-actions]_button:has(>svg):hover]:!bg-neutral-800 dark:[&_[data-streamdown=code-block-actions]_button:has(>svg):hover]:!text-neutral-50",

	// ── Links ────────────────────────────────────────────────────────────────
	// Streamdown asks for `text-primary`, which resolves to #AFA3FE here rather
	// than the brand violet. Pinned to its own token (#7F6DF1) so it cannot drift
	// with whatever `primary` means in a consuming app, while a dark theme can
	// still lighten it.
	"[&_[data-streamdown=link]]:!text-chat-link",

	// ── Lists ────────────────────────────────────────────────────────────────
	// A list item came out at 13px against 14.6px body copy, so a bullet read a
	// size smaller than the paragraph above it. Inherit instead of naming a size,
	// so the two cannot drift apart again.
	"[&_li]:![font-size:inherit]",

	// ── Headings ─────────────────────────────────────────────────────────────
	// Normal weight, so a heading marks a section without shouting.
	//
	// The sizes are set here too, and that is not cosmetic drift: streamdown asks
	// for `text-2xl`/`text-xl`, neither of which Caruso's Tailwind generates, so
	// every heading fell back to 13px — SMALLER than the 14.6px body. Dropping the
	// weight without this would leave a heading lighter and smaller than the
	// paragraph under it. Relative units, so they track the body size.
	"[&_h1]:!font-normal [&_h2]:!font-normal [&_h3]:!font-normal [&_h4]:!font-normal",
	"[&_h1]:!text-[1.25em] [&_h2]:!text-[1.15em] [&_h3]:!text-[1.05em] [&_h4]:!text-[1em]",
].join(" ");
